import React from "react";
import { useHumanInTheLoop } from "@copilotkit/react-core/v2";
import { z } from "zod";
import { useAppSelector } from "src/stores/redux/hooks";
import { getAppFQN, getNodes } from "src/modules/flow-designer/react-flow/helpers/selectors";
import { getComponentId } from "src/modules/flow-designer/react-flow/helpers/component.utils";
import componentService, { ComponentKeys } from "core/services/componentService/component-service";
import utils from "core/utils";
import { AdapterPickerTool, AdapterPickerToolArgs } from "./adapter-picker/adapter-picker-tool";
import { HitlSummary } from "./hitl-summary";
import store from "src/stores/redux/store";

const SOURCE_TYPES = ["SOURCE", "EXTERNALSOURCE"];

const parameters = z.object({
    type: z.enum(["source", "target"]).describe("Whether to show sources or targets."),
    sourceName: z
        .string()
        .optional()
        .describe(
            "Human-readable source name exactly as returned by a prior pickAdapter selection (e.g. 'Oracle Database'), required when type='target'. Omit for type='source'."
        )
});

export const useAdapterPickerTool = () => {
    const appFQN = useAppSelector(getAppFQN);
    const isOnFlowDesigner = Boolean(appFQN);

    const deriveExistingSourceName = (): string | null => {
        try {
            const nodes = store.getState().flowDesigner.nodes;
            if (!nodes) return null;
            const sourceNode = nodes.find(n => SOURCE_TYPES.includes(n.data?.type?.toUpperCase()));
            if (!sourceNode) return null;
            const componentId: any =
                sourceNode.data.metaObjectId === "temp-node"
                    ? sourceNode.data.componentId
                    : getComponentId(sourceNode.data.metaObject, utils.ENTITIES.SOURCE);
            if (!componentId) return null;
            return componentService.getMetadata(componentId)?.[ComponentKeys.HumanReadableName];
        } catch {
            return null;
        }
    };

    useHumanInTheLoop({
        name: "pickAdapter",
        description:
            "Displays an interactive picker so the user can browse and click a source or target adapter " +
            "(e.g. Oracle, Snowflake, Kafka). Call this when the user asks to 'add a source' or 'add a target' " +
            "without specifying which adapter. If the user asks to add a TARGET, call this directly with " +
            "type='target' — do NOT call it for type='source' first just to obtain a sourceName. Omit sourceName " +
            "entirely unless you already have it from a 'selected' result of a prior type='source' call earlier " +
            "in this same conversation; if the app already has a source on the canvas, it will be detected " +
            "automatically. Returns the selected adapter's id/value/adapter name for use with getTqlSkeleton, or " +
            '{ "status": "cancelled" } / { "status": "missing-source" } (only possible if the app truly has no ' +
            "source anywhere).",
        parameters,
        available: isOnFlowDesigner ? "enabled" : "disabled",
        render: ({
            args,
            respond,
            status,
            name,
            result
        }: {
            args: AdapterPickerToolArgs;
            respond: (r: string) => void;
            status: string;
            name: string;
            result?: string;
        }) => {
            if (status === "complete") return <HitlSummary toolName={name} result={result ?? ""} />;
            if (status !== "executing") return <></>;
            const resolvedArgs =
                args.type === "target" && !args.sourceName ? { ...args, sourceName: deriveExistingSourceName() } : args;
            return <AdapterPickerTool args={resolvedArgs} respond={respond} />;
        }
    });
};
